import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Bot, Boxes, Check, ClipboardCheck, Info, UserRound, CalendarClock, ChevronDown, Clock, Layers, LayoutGrid, Plus, Puzzle, Target, Telescope, UserPlus, Users, Workflow, X,
} from 'lucide-react';
import { VERB_LABELS } from '@paradigm/permissions/constants/formA';
import { Modal } from '@ofself/shell';
import YAML from 'yaml';
import api from '../../services/api';
import { Section, Decide } from '../../components/grant/parts';
import { ConsentFrame, ViewTabs } from '../../components/nucleus/GrantViews';
import Segmented from '../../components/nucleus/Segmented';
import Picker from '../../components/nucleus/Picker';
import { FindBar } from '../../components/nucleus/FacetRail';
import AccessList from '../../components/nucleus/AccessList';
import { loadPluginList } from '../plugins/data/items';
import { onlyPluginsLine } from '../plugins/data/elsewhere';
import { NodeMark, Glyphed } from '../../components/nucleus/faces';
import { RealmRings } from '../../components/nucleus/cells';
import { spaceWords } from '../../nucleus/access';
import { FILTER_OPS, OPS_BY_KIND, kindOfField, conditionsOf, filterOf, filterWords, VALUELESS, SPAN_UNITS } from '../../nucleus/filters';
import { plural } from '../../nucleus/format';

// THE REALM EDITOR: one page, in the consent page's own parts, that walks you
// through making a realm, in the order Help names a realm's parts: its name and
// colour, its nodes (by schema and individual), its plugins, its connections and
// activity, and how you appear. The YAML folds away at the foot.
//
// A realm's permissions are its CEILING, the canonical access blob
//   { nodes: { read: {schemas: {<id>: {…mask}}, nodes: [...]}, create, edit, delete, propose },
//     plugins: {execute: {plugin_ids?, tools?}}, discovery: {friends, groups, realms},
//     activity: {read}, automations: {write, execute}, sub_entity: {declare} }
// and it travels as `ceiling_yaml`, the blob's JSON, which is valid YAML.
//
// The blob is verb-first; the page reads it schema-first (one row per schema,
// its verbs as the consent page's tags) and writes each tap back as one change
// to one verb's scope. THE RULE: the editor never widens or drops what it did
// not touch. A field mask, a filter, a plugin's tool modes, a scope key it has no
// control for: all ride along. An emptied scope would read as "everything", so a
// verb whose last schema goes is removed, never left empty.
//   create  POST <base> {name, description, color, ceiling_yaml, profile_node_id?}
//   edit    POST <base>/<id>/preview-impact → confirm → PUT <base>/<id>
//   import  POST /privacy-realms/parse-yaml {yaml}

// in the consent page's order (components/grant/Ask.js KEY_ORDER)
export const NODE_VERBS = ['read', 'edit', 'create', 'delete', 'propose'];
// the other access a realm can cover is named as the consent page names it (formA VERB_LABELS)
// (in a group, the group's: "Book Club’s plugins", never "your")
const otherHint = (w) => ({
    'plugins.execute': `Use ${w.your} plugins’ tools, in this realm.`,
    'plugins.prepare': w.inGroup ? 'Fill in a run of a tool for an admin to allow. It cannot start one itself.' : 'Fill in a run of a tool for you to allow. It cannot start one itself.',
    'discovery.friends': 'Your friend list.',
    'discovery.groups': 'The groups you are in.',
    'discovery.realms': w.inGroup ? `The names of realms ${w.you} made discoverable.` : 'The names of realms you made discoverable.',
    'activity.read': `What apps made, read and changed across ${w.your} data.`,
    'automations.write': `Make automations that act on ${w.your} data.`,
    'automations.execute': 'Let automations run, automatically or when something changes.',
    'sub_entity.declare': 'Agents of its own, never with more than this realm allows.',
});
const verbHint = (w) => ({
    read: 'see it', create: 'add new nodes', edit: 'change it', delete: 'remove it', propose: w.inGroup ? 'suggest changes an admin approves' : 'suggest changes you approve',
});
// what each verb lets them do, in full, for the ⓘ beside Nodes
const verbMeans = (w) => [
    ['read', w.inGroup ? 'See the node, and the fields allowed.' : 'See the node, and the fields you allow.'],
    ['edit', 'Change nodes it can see.'],
    ['create', `Add new nodes of that schema to ${w.your} graph.`],
    ['delete', 'Remove nodes it can see.'],
    ['propose', w.inGroup ? 'Suggest new nodes or changes. Nothing applies until an admin approves it.' : 'Suggest new nodes or changes. Nothing applies until you approve it.'],
];
// a group's realm never reaches a person's own friends or groups: those are personal
const PERSONAL_ONLY = new Set(['discovery.friends', 'discovery.groups']);
// the words of the space the realm is made in (your own, or a group's)
const Words = createContext(spaceWords(null));
// COLOURS, named, and enough that no two realms need share one. The first eight
// are the backend's own palette (models/privacy.py), in the order it assigns them.
export const COLOURS = [
    ['#4F8DFD', 'Blue'], ['#22C55E', 'Green'], ['#F59E0B', 'Amber'], ['#EF4444', 'Red'],
    ['#A855F7', 'Purple'], ['#06B6D4', 'Cyan'], ['#EC4899', 'Pink'], ['#84CC16', 'Lime'],
    ['#6366F1', 'Indigo'], ['#14B8A6', 'Teal'], ['#F97316', 'Orange'], ['#F43F5E', 'Rose'],
    ['#8B5CF6', 'Violet'], ['#0EA5E9', 'Sky'], ['#10B981', 'Emerald'], ['#EAB308', 'Yellow'],
    ['#D946EF', 'Fuchsia'], ['#FF7F6B', 'Coral'], ['#8FA376', 'Moss'], ['#A16207', 'Ochre'],
    ['#9D4EDD', 'Plum'], ['#1E3A8A', 'Navy'], ['#6B8E23', 'Olive'], ['#64748B', 'Slate'],
];
const colourName = (hex) => COLOURS.find(([c]) => c.toLowerCase() === String(hex || '').toLowerCase())?.[1] || hex;
/** The first colour none of `taken` (lower-case hex) uses; the first of all when every one is. */
export const autoColour = (taken) => (COLOURS.find(([c]) => !taken.has(c.toLowerCase())) || COLOURS[0])[0];
// BEYOND NODES: what a realm can allow besides its nodes, each added only when
// wanted. Whatever is not added is a no.
// EVERY verb the backend enforces has a row here or in NODE_VERBS: one missing is
// held, enforced and impossible to see or take off (the test checks formA).
export const PERMISSIONS = [
    ['plugins', 'execute', Puzzle], ['plugins', 'prepare', ClipboardCheck], ['discovery', 'friends', Users], ['discovery', 'groups', Boxes],
    ['discovery', 'realms', Telescope], ['activity', 'read', Clock], ['automations', 'write', Workflow],
    ['automations', 'execute', CalendarClock], ['sub_entity', 'declare', Bot],
];

const clone = (o) => JSON.parse(JSON.stringify(o ?? {}));
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const sortKeys = (v) => (Array.isArray(v) ? v.map(sortKeys)
    : v && typeof v === 'object' ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, sortKeys(v[k])])) : v);
const sameJSON = (a, b) => JSON.stringify(sortKeys(a ?? {})) === JSON.stringify(sortKeys(b ?? {}));
export const yamlOf = (blob) => JSON.stringify(sortKeys(blob), null, 2);
// the same realm as a person reads it, in YAML (what is SAVED is yamlOf: JSON is
// YAML too, and keeps the server's parser to the strictest form)
export const yamlShown = (blob) => YAML.stringify(sortKeys(blob), { indent: 2 });
/** The realm as a whole doc, the shape the paste box reads back: name, what it is for, ceiling. */
export const realmDocYaml = ({ name, description, ceiling }) => YAML.stringify({
    name: name || '', ...(description ? { description } : {}), ceiling: sortKeys(ceiling || {}),
}, { indent: 2 });

/** `nodes.edit_content` is the legacy spelling of `nodes.edit` (the backend folds it):
 *  read as `edit`, so it shows as edit and taking edit off takes it off. */
export function foldLegacyVerbs(blob) {
    const legacy = blob?.nodes?.edit_content;
    if (legacy === undefined) return blob;
    const next = clone(blob);
    delete next.nodes.edit_content;
    const edit = next.nodes.edit;
    // either one reaching every node reaches every node; otherwise the scopes join
    if (edit === undefined) next.nodes.edit = isObj(legacy) ? legacy : {};
    else if (!reachesAll(edit) && !reachesAll(isObj(legacy) ? legacy : {})) {
        const map = (scope) => (isObj(scope.schemas) ? scope.schemas : Object.fromEntries(schemaIdsOf(scope).map((x) => [x, {}])));
        const schemas = { ...map(legacy), ...map(edit) };
        const nodes = [...new Set([...nodeIdsOf(legacy), ...nodeIdsOf(edit)])];
        next.nodes.edit = { ...legacy, ...edit, ...(Object.keys(schemas).length ? { schemas } : {}), ...(nodes.length ? { nodes } : {}) };
    } else next.nodes.edit = {};
    return next;
}

const schemaIdsOf = (scope) => {
    const s = scope?.schemas;
    return Array.isArray(s) ? s.map(String) : isObj(s) ? Object.keys(s) : [];
};
const nodeIdsOf = (scope) => (Array.isArray(scope?.nodes) ? scope.nodes.map(String) : []);
// a verb present with no schemas and no nodes named reaches every node
const reachesAll = (scope) => scope !== undefined && !schemaIdsOf(scope).length && !nodeIdsOf(scope).length;

/** The blob read schema-first: the verbs over all data, per schema and per node, and read's masks. */
export function holdingsOf(blob) {
    const nodes = isObj(blob?.nodes) ? blob.nodes : {};
    const all = [];
    const schemas = new Map();
    const singles = new Map();
    for (const verb of NODE_VERBS) {
        const scope = nodes[verb];
        if (scope === undefined) continue;
        if (reachesAll(scope)) { all.push(verb); continue; }
        for (const id of schemaIdsOf(scope)) schemas.set(id, (schemas.get(id) || new Set()).add(verb));
        for (const id of nodeIdsOf(scope)) singles.set(id, (singles.get(id) || new Set()).add(verb));
    }
    const masks = isObj(nodes.read?.schemas) ? nodes.read.schemas : {};
    return { all, schemas, singles, masks };
}

// one verb's scope, rewritten by `fn` (undefined in, undefined out = the verb is absent)
function withScope(blob, verb, fn) {
    const next = clone(blob);
    const nodes = isObj(next.nodes) ? next.nodes : {};
    const was = nodes[verb] === undefined ? undefined : (isObj(nodes[verb]) ? nodes[verb] : {});
    const out = fn(was);
    if (out === undefined) delete nodes[verb]; else nodes[verb] = out;
    if (Object.keys(nodes).length) next.nodes = nodes; else delete next.nodes;
    return next;
}
// an emptied scope would read as "everything": the verb goes instead
const settle = (s) => {
    if (!schemaIdsOf(s).length) delete s.schemas;
    if (!nodeIdsOf(s).length) delete s.nodes;
    return reachesAll(s) ? undefined : s;
};
const maskFrom = (orig, verb, id) => {
    const m = orig?.nodes?.[verb]?.schemas;
    return isObj(m) && isObj(m[id]) ? clone(m[id]) : {};
};

/** Allow or stop one verb on one schema. Switching one back on returns the mask it opened with. */
export function setSchemaVerb(blob, id, verb, on, orig = null) {
    return withScope(blob, verb, (scope) => {
        if (on) {
            if (scope === undefined) return { schemas: { [id]: maskFrom(orig, verb, id) } };
            if (reachesAll(scope)) return scope;
            const s = { ...scope };
            if (Array.isArray(s.schemas)) { if (!s.schemas.map(String).includes(id)) s.schemas = [...s.schemas, id]; }
            else {
                const map = isObj(s.schemas) ? s.schemas : {};
                s.schemas = { ...map, [id]: map[id] || maskFrom(orig, verb, id) };
            }
            return s;
        }
        if (scope === undefined || reachesAll(scope)) return scope;
        const s = { ...scope };
        if (Array.isArray(s.schemas)) s.schemas = s.schemas.filter((x) => String(x) !== id);
        else if (isObj(s.schemas)) { const { [id]: _gone, ...rest } = s.schemas; s.schemas = rest; }
        return settle(s);
    });
}

/** Allow or stop one verb on one node. */
export function setNodeVerb(blob, id, verb, on) {
    return withScope(blob, verb, (scope) => {
        if (on) {
            if (scope === undefined) return { nodes: [id] };
            if (reachesAll(scope)) return scope;
            return nodeIdsOf(scope).includes(id) ? scope : { ...scope, nodes: [...nodeIdsOf(scope), id] };
        }
        if (scope === undefined || reachesAll(scope)) return scope;
        return settle({ ...scope, nodes: nodeIdsOf(scope).filter((x) => x !== id) });
    });
}

/** One verb over all of your data, or not at all. */
export function setAllVerb(blob, verb, on) {
    return withScope(blob, verb, (scope) => {
        if (!on) return undefined;
        const s = isObj(scope) ? { ...scope } : {};
        delete s.schemas; delete s.nodes;
        return s;
    });
}

/** Only these fields of a schema are read (null: every field). */
export function setFields(blob, id, fields) {
    return withScope(blob, 'read', (scope) => {
        if (scope === undefined || reachesAll(scope)) return scope;
        const s = { ...scope };
        const map = Array.isArray(s.schemas) ? Object.fromEntries(s.schemas.map((x) => [String(x), {}])) : { ...(s.schemas || {}) };
        const m = { ...(map[id] || {}) };
        if (fields) m.fields = fields; else delete m.fields;
        map[id] = m;
        s.schemas = map;
        return s;
    });
}

/** LINKS GO WITH NODES: whatever reads nodes from a realm also sees the links
 *  between the ones it reads (both ends must be held for a link to show). */
export function withLinks(blob) {
    const read = blob?.nodes?.read;
    if (read === undefined || (isObj(read) && read.include_refs)) return blob;
    const next = clone(blob);
    next.nodes.read = { ...(isObj(read) ? read : {}), include_refs: true };
    return next;
}

/** All nodes, read: what "All nodes" starts from. The links setting is kept. */
export function everyNode(blob) {
    const next = clone(blob);
    const refs = !!blob?.nodes?.read?.include_refs;
    next.nodes = { read: refs ? { include_refs: true } : {} };
    return next;
}

/** Only the entries where (filter: { field: value } or { field: { op: value } }); null: every entry. */
export function setFilter(blob, id, filter) {
    return withScope(blob, 'read', (scope) => {
        if (scope === undefined || reachesAll(scope)) return scope;
        const s = { ...scope };
        const map = Array.isArray(s.schemas) ? Object.fromEntries(s.schemas.map((x) => [String(x), {}])) : { ...(s.schemas || {}) };
        const m = { ...(map[id] || {}) };
        if (filter && Object.keys(filter).length) m.filter = filter; else delete m.filter;
        map[id] = m;
        s.schemas = map;
        return s;
    });
}

// A filter as conditions and back, and in words, lives in nucleus/filters.js: the
// panels say a filter the way this editor writes it.
export { FILTER_OPS, OPS_BY_KIND, kindOfField, conditionsOf, filterOf } from '../../nucleus/filters';

/** Toggle one non-node verb; switching it back on returns exactly what it had (an allowlist, tool modes). */
export function setVerb(blob, res, verb, on, { orig = null } = {}) {
    const next = clone(blob);
    const r = isObj(next[res]) ? { ...next[res] } : {};
    if (on) r[verb] = orig && isObj(orig[res]) && orig[res][verb] !== undefined ? clone(orig[res][verb]) : {};
    else delete r[verb];
    if (Object.keys(r).length) next[res] = r; else delete next[res];
    return next;
}

// schemas are counted by family: the versions of one schema are one schema
const heldWords = (held, keyOf = (id) => id) => [
    held.all.length ? 'all nodes' : null,
    held.schemas.size ? plural(new Set([...held.schemas.keys()].map(keyOf)).size, 'schema') : null,
    held.singles.size ? plural(held.singles.size, 'node') : null,
].filter(Boolean).join(' · ') || null;
const orderOf = (h) => ({ schemas: [...h.schemas.keys()], nodes: [...h.singles.keys()] });
// the rows in their order, then any the blob holds that the order has not met (an import)
const withRest = (order, map) => [...order, ...[...map.keys()].filter((id) => !order.includes(id))];
const nameOfNode = (n) => n?.value_json?.title || n?.title || 'Untitled';

export default function RealmsEditor({
    realm, base, schemaNames = {}, schemaKeys = {}, onDone, readOnly = false, faces = [], mayEditFace = true,
    groupId = null, spaceName = null, defaultFaceId = null, loadTitles, realms = [], onFacesChanged,
}) {
    const w = useMemo(() => spaceWords(groupId, spaceName), [groupId, spaceName]);
    const orig = useMemo(() => (realm ? foldLegacyVerbs(clone(realm.ceilingBlob)) : {}), [realm]);
    const [name, setName] = useState(realm?.name || '');
    const [description, setDescription] = useState(realm?.description || '');
    const [color, setColor] = useState(realm?.color || null);
    // a new realm starts EMPTY: it holds nothing until you say so
    const [blob, setBlob] = useState(() => clone(orig));
    const [faceId, setFaceId] = useState(realm?.profileNodeId || '');
    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);
    const [impact, setImpact] = useState(null);
    const [review, setReview] = useState('summary');     // the last step: summary | yaml
    const [importing, setImporting] = useState(false);
    const [yamlText, setYamlText] = useState('');
    const [undoImport, setUndoImport] = useState(null);     // what the realm was before a paste replaced it
    const [leaving, setLeaving] = useState(false);          // asked to close with changes unsaved
    const [nodeInfo, setNodeInfo] = useState({});           // node id → { title, schema }
    const [madeFaces, setMadeFaces] = useState([]);         // identities made from this sheet, until the list reloads
    const edit = (fn) => { if (!readOnly) setBlob(fn); };

    // ONE SCHEMA, EVERY VERSION. Each version of a schema is its own row (and its
    // own id), and a rule matches a node by the exact id it was saved under, so a
    // rule on the newest version alone would miss every node on an older one. The
    // picker lists each schema once; picking it, and every row's verbs, fields and
    // filters, cover all its versions together. A schema's versions share its
    // schema_name (schemaKeys) — not its display name, which two schemas may share:
    // those stay two, each told apart by its schema_name.
    const keyOf = useCallback((id) => schemaKeys[id] || schemaNames[id] || id, [schemaKeys, schemaNames]);
    const families = useMemo(() => {
        const m = new Map();
        for (const id of Object.keys(schemaNames)) m.set(keyOf(id), [...(m.get(keyOf(id)) || []), id]);
        return m;
    }, [schemaNames, keyOf]);
    const familyOf = useCallback((id) => families.get(keyOf(id)) || [id], [families, keyOf]);
    const labelOf = useMemo(() => {
        const keysByName = new Map();
        for (const [key, ids] of families) {
            const n = schemaNames[ids[0]] || key;
            keysByName.set(n, new Set([...(keysByName.get(n) || []), key]));
        }
        return (id) => {
            const n = schemaNames[id];
            if (!n) return null;
            return keysByName.get(n)?.size > 1 ? `${n} (${keyOf(id)})` : n;
        };
    }, [families, schemaNames, keyOf]);
    const schemas = useMemo(() => [...families.entries()].map(([key, ids]) => ({ id: ids[0], ids, key, name: labelOf(ids[0]) }))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())), [families, labelOf]);
    const held = holdingsOf(blob);
    // THE ROWS KEEP THEIR PLACE. The blob is verb-first, so reading it back puts a
    // schema wherever its first verb lands; a row whose read went off would jump.
    // The rows are listed in the order they were added, and a row with no verb
    // left stays (its tags all off) until × takes it away.
    const [rowOrder, setRowOrder] = useState(() => orderOf(holdingsOf(orig)));
    const schemaRows = withRest(rowOrder.schemas, held.schemas);
    const nodeRows = withRest(rowOrder.nodes, held.singles);
    // EVERY NODE OR SOME: with every node held, a schema or a single node only
    // repeats it, so the step is one or the other. Going to every node keeps what
    // some held aside, and coming back restores it.
    const [mode, setMode] = useState(() => (holdingsOf(orig).all.length ? 'all' : 'some'));
    const kept = useRef(null);
    const [addKey, setAddKey] = useState(0);            // the add list closes when the mode changes
    const chooseMode = (next) => {
        if (readOnly || next === mode) return;
        setAddKey((k) => k + 1);
        if (next === 'all') {
            kept.current = { nodes: clone(blob.nodes || {}), order: rowOrder };
            setBlob((b) => everyNode(b));
        } else {
            const back = kept.current;
            kept.current = null;
            setBlob((b) => {
                const n = clone(b);
                if (back && Object.keys(back.nodes).length) n.nodes = back.nodes; else delete n.nodes;
                return n;
            });
            setRowOrder(back ? back.order : { schemas: [], nodes: [] });
        }
        setMode(next);
    };
    const addRow = (kind, id) => setRowOrder((o) => (o[kind].includes(id) ? o : { ...o, [kind]: [...o[kind], id] }));
    const dropRow = (kind, id) => setRowOrder((o) => ({ ...o, [kind]: o[kind].filter((x) => x !== id) }));
    const singleIds = [...held.singles.keys()].join(',');
    // the colours your other realms wear, and who wears each
    const taken = useMemo(() => {
        const m = new Map();
        for (const r of realms) {
            if (!r.color || (realm && String(r.id) === String(realm.id))) continue;
            const k = r.color.toLowerCase();
            m.set(k, [...(m.get(k) || []), r.name]);
        }
        return m;
    }, [realms, realm]);
    const auto = autoColour(taken);

    // the titles of the single nodes it holds
    useEffect(() => {
        const missing = singleIds ? singleIds.split(',').filter((id) => !nodeInfo[id]) : [];
        if (!missing.length || !loadTitles) return undefined;
        let live = true;
        // one read for all of them; one it cannot read is 'A node', as a failed read was
        const asked = missing.slice(0, 24);
        loadTitles(asked).catch(() => ({}))
            .then((t) => { if (live) setNodeInfo((m) => ({ ...m, ...Object.fromEntries(asked.map((id) => [id, { title: t?.[id] || 'A node' }])) })); });
        return () => { live = false; };
    }, [singleIds, loadTitles]); // eslint-disable-line react-hooks/exhaustive-deps


    const save = async (confirmed = false) => {
        setWorking(true);
        setError(null);
        const final = withLinks(blob);
        const ceilingChanged = !realm || !sameJSON(final, orig);
        const yaml = yamlOf(final);
        const body = { name: name.trim(), description };
        if (ceilingChanged) body.ceiling_yaml = yaml;
        const wantColour = color || auto;
        if (wantColour !== (realm?.color || null)) body.color = wantColour;
        const wantFace = faceId || null;
        if (mayEditFace && wantFace !== (realm?.profileNodeId || null)) body.profile_node_id = wantFace;
        try {
            if (realm) {
                // BOTH DIRECTIONS, before anything is saved: who would lose access
                // (preview-impact) and who would GAIN it (preview-widening). Only the
                // first was asked, so adding a schema to a shared realm went live to
                // every grant it feeds with no warning at all.
                if (!confirmed && ceilingChanged) {
                    const named = (x) => [x.entity_name || x.entity_type, x.sub_entity_key].filter(Boolean).join(' / ');
                    const [narrow, widen] = await Promise.all([
                        api.request(`${base}/${realm.id}/preview-impact`, {
                            method: 'POST', body: { ceiling_yaml: yaml, excluded_node_ids: realm.excludedNodeIds || [] },
                        }),
                        api.request(`${base}/${realm.id}/preview-widening`, { method: 'POST', body: { access: final } })
                            .catch(() => null),
                    ]);
                    const narrows = (narrow?.affected_exposure_profiles || []).map(named).filter(Boolean);
                    const widens = (widen?.gaining || []).map(named).filter(Boolean);
                    if (narrows.length || widens.length) { setImpact({ narrows, widens }); setWorking(false); return; }
                }
                await api.request(`${base}/${realm.id}`, { method: 'PUT', body });
            } else {
                await api.request(base, { method: 'POST', body });
            }
            setWorking(false);
            onDone(true);
        } catch (e) {
            setError(e?.message || 'Could not save');
            setWorking(false);
        }
    };

    // THE PASTE BOX STARTS FROM THIS REALM, so a paste is an edit of what is here,
    // not a blank page; and a paste that replaced everything can be taken back.
    const openImport = () => {
        setYamlText(realmDocYaml({ name, description, ceiling: withLinks(blob) }));
        setImporting(true);
    };
    const importYAML = async () => {
        try {
            const j = await api.request('/privacy-realms/parse-yaml', { method: 'POST', body: { yaml: yamlText } });
            if (j?.errors?.length) { setError(j.errors.join('\n')); return; }
            setUndoImport({ name, description, faceId, blob, rowOrder, mode });
            if (j?.name) setName(j.name);
            if (j?.description != null) setDescription(j.description);
            if (j?.profile_node_id !== undefined && mayEditFace) setFaceId(j.profile_node_id || '');
            const pasted = foldLegacyVerbs(clone(j?.ceiling || {}));
            setBlob(pasted);
            setRowOrder(orderOf(holdingsOf(pasted)));
            setMode(holdingsOf(pasted).all.length ? 'all' : 'some');
            setImporting(false);
            setError(null);
        } catch (e) { setError(e?.message || 'Could not read that YAML'); }
    };
    const undoPaste = () => {
        const u = undoImport;
        if (!u) return;
        setName(u.name); setDescription(u.description); setFaceId(u.faceId);
        setBlob(u.blob); setRowOrder(u.rowOrder); setMode(u.mode);
        setUndoImport(null);
    };

    // CLOSING NEVER LOSES WORK SILENTLY: with anything changed, it asks first.
    const dirty = !readOnly && (
        name !== (realm?.name || '') || description !== (realm?.description || '')
        || (color || null) !== (realm?.color || null) || (faceId || '') !== (realm?.profileNodeId || '')
        || !sameJSON(blob, orig));
    const close = () => { if (dirty) setLeaving(true); else onDone(false); };

    const hue = color || auto;
    const shown = name.trim();
    // read only: a default realm, a group's built-in No access, or a realm a
    // member reads but may not change — each said as what it is
    const readWord = realm?.builtin ? 'built in' : realm?.system ? 'a default realm' : null;
    const readLead = realm?.builtin === 'no_access' ? `${realm.name} is built in and grants nothing.`
        : realm?.system ? 'You can duplicate it, but not change it.'
            : spaceName ? `Only an admin of ${spaceName} can change it.` : 'You can duplicate it, but not change it.';
    const label = readOnly ? (readWord ? `${realm?.name}, ${readWord}` : realm?.name || 'Realm') : realm ? `Edit ${realm.name}` : 'New realm';
    const onCount = (keys) => keys.filter(([res, verb]) => isObj(blob[res]) && blob[res][verb] !== undefined).length;

    // how it presents you: your identities, in the consent page's rows
    const identities = [...faces, ...madeFaces.filter((m) => !faces.some((f) => String(f.id) === m.id))].map((f) => ({
        id: String(f.id), name: f.name, avatar_url: f.node?.avatar_url || null, is_default: String(f.id) === String(defaultFaceId || ''),
    }));
    // NOTHING IS PICKED FOR YOU: a new realm names its identity when you choose one.
    // A realm saved without one shows your default, and may keep doing so.
    const faceShown = faceId ? String(faceId) : null;
    const needFace = !realm && !readOnly && mayEditFace && identities.length > 0;
    const faceName = (id) => identities.find((i) => i.id === id)?.name;

    // THE OUTLINE: each step, whether it is done, and where you are
    const scroller = useRef(null);
    const [at, setAt] = useState(1);
    const spy = () => {
        const box = scroller.current;
        if (!box) return;
        const steps = [...box.querySelectorAll('.re__step')];
        if (box.scrollTop + box.clientHeight >= box.scrollHeight - 4) { setAt(steps.length); return; }
        let cur = 1;
        for (const el of steps) if (el.offsetTop - box.scrollTop <= 96) cur = Number(el.dataset.step);
        setAt(cur);
    };
    const goTo = (n) => {
        const box = scroller.current;
        const el = box?.querySelector(`[data-step="${n}"]`);
        if (el) box.scrollTo({ top: el.offsetTop - 8, behavior: 'smooth' });
    };
    const nodesSaid = heldWords(held, keyOf);
    const beyond = onCount(PERMISSIONS);
    // nodes are optional: a realm may hold only permissions, or nothing (as No Access does)
    const ready = !!shown && (!needFace || !!faceShown);
    const grantsNothing = !nodesSaid && !beyond;
    const appearTitle = w.inGroup ? `How ${w.you} appears` : 'How you appear';
    const outline = [
        { n: 1, title: 'Name it', done: !!shown, say: shown || 'Not named yet' },
        { n: 2, title: appearTitle, done: !!faceShown || (!!realm && !needFace), say: faceShown ? faceName(faceShown) : realm ? `${w.Your} default identity` : 'Not chosen yet' },
        { n: 3, title: 'Nodes', optional: true, done: !!nodesSaid, say: nodesSaid || 'None · optional' },
        { n: 4, title: `Beyond ${w.your} nodes`, optional: true, done: beyond > 0, say: beyond ? plural(beyond, 'permission') : 'None · optional' },
        { n: 5, title: 'Check it', done: ready, say: !ready ? (!shown ? 'Name it first' : (w.inGroup ? `Choose how ${w.you} appears` : 'Choose how you appear')) : grantsNothing ? 'Ready · it grants nothing' : (realm ? 'Ready to save' : 'Ready to create') },
    ];

    return (
        <Words.Provider value={w}>
        <Modal label={label} onClose={close} className="re">
            <ConsentFrame>
                <div className="re__body" style={{ '--re-hue': hue }}>
                <nav className="re__outline" aria-label="Steps">
                    <span className="re__outline-head">{readOnly ? (realm?.builtin ? 'Built in' : realm?.system ? 'A default realm' : 'A realm') : realm ? 'Changing a realm' : 'Making a realm'}</span>
                    <ol>
                        {outline.map((x) => (
                            <li key={x.n}>
                                <button type="button" aria-current={at === x.n ? 'step' : undefined}
                                    className={`re__outline-step${x.done ? ' is-done' : ''}${x.optional ? ' is-optional' : ''}`} onClick={() => goTo(x.n)}>
                                    <span className="re__mark" aria-hidden="true">{x.done && <Check size={11} strokeWidth={2.4} />}</span>
                                    <span className="re__outline-text">
                                        <b>{x.title}</b>
                                        <small>{x.say}</small>
                                    </span>
                                    <span className="sr-only">{x.done ? 'done' : x.optional ? 'optional' : 'not done'}</span>
                                </button>
                            </li>
                        ))}
                    </ol>
                </nav>
                <div className="re__scroll" ref={scroller} onScroll={spy}>
                    <header className="re__mast" style={{ '--re-hue': hue }}>
                        <span className="re__rings"><RealmRings color={hue} /></span>
                        <h2 className="gp-explain__h re__h">
                            {readOnly ? <><b>{realm?.name}</b>{readWord ? `, ${readWord}.` : '.'}</>
                                : realm ? <>Change what <b>{shown || realm.name}</b> holds.</>
                                    : shown ? <>Make <b>{shown}</b>.</> : 'Make a realm.'}
                        </h2>
                        <p className="re__lead">
                            {readOnly ? readLead
                                : realm ? 'Whatever is shared from it follows your change on its next request.'
                                    : w.inGroup
                                        ? `A realm is a part of ${w.you} to share from. Choose what it holds once, then add it to any app, person or member that asks.`
                                        : 'A realm is a part of you to share from. Choose what it holds once, then add it to any app, person or group that asks.'}
                        </p>
                    </header>

                    {importing ? (
                        <Section n="YAML" title="Paste a realm" hint="This is the realm as it stands. Change it, or paste another over it: its name, what it is for and everything it holds become what the YAML says. You can undo it after.">
                            <textarea className="re__yaml-in" value={yamlText} onChange={(e) => setYamlText(e.target.value)}
                                placeholder={'name: Journal\nceiling:\n  nodes:\n    read: {}'} aria-label="Realm YAML" autoFocus />
                            <div className="re__row-end">
                                <button type="button" className="gp-link re__quiet" onClick={() => { setImporting(false); setError(null); }}>Cancel</button>
                                <button type="button" className="re__btn" onClick={importYAML} disabled={!yamlText.trim()}>Use this YAML</button>
                            </div>
                        </Section>
                    ) : (<>
                        <div className="re__step" data-step="1">
                        <Section n="01" title="Name it" hint={readOnly ? null : w.inGroup
                            ? `Call it what ${w.you} would call this part of itself: Editors, Events, Money. Anyone it is shared with sees the name and the line under it.`
                            : 'Call it what you would call this part of your life: Health, Work, Family. Anyone you share it with sees the name and the line under it.'}>
                            <label className="re__field is-name">
                                <span className="re__minor">Name</span>
                                <input value={name} placeholder="Journal, Work, Family…" readOnly={readOnly}
                                    onChange={(e) => setName(e.target.value)} aria-label="Name" autoFocus={!readOnly && !realm} />
                            </label>
                            <label className="re__field">
                                <span className="re__minor">What it is for</span>
                                <input value={description} placeholder={w.inGroup ? 'A line anyone it is shared with will see' : 'A line anyone you share it with will see'} readOnly={readOnly}
                                    onChange={(e) => setDescription(e.target.value)} aria-label="What it is for" />
                            </label>
                            {!readOnly && <ColourPicker value={color} auto={auto} taken={taken} onChange={setColor} />}
                        </Section>
                        </div>

                        <div className="re__step" data-step="2">
                        <Section n="02" title={appearTitle} hint={readOnly ? null : w.inGroup
                            ? `Who anything shared from this realm sees ${w.you} as. Pick one of its identities, or make a new one for it. A contract can still pick its own.`
                            : 'Who anything shared from this realm sees you as. Pick one of your identities, or make a new one for it. A contract can still pick its own.'}>
                            {identities.length ? (
                                <div className="gp-face__list re__faces" role="radiogroup" aria-label={appearTitle}>
                                    {identities.map((i) => {
                                        const on = faceShown === i.id;
                                        return (
                                            <button key={i.id} type="button" role="radio" aria-checked={on} disabled={readOnly || !mayEditFace}
                                                className={`gp-face__opt${on ? ' is-on' : ''}`} onClick={() => setFaceId(i.id)}>
                                                <FaceTile identity={i} />
                                                <span className="gp-face__opt-name">{i.name}</span>
                                                {i.is_default && <span className="gp-face__opt-note">{w.inGroup ? 'the default' : 'your default'}</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : <p className="re__empty">No identity yet. Make one below.</p>}
                            {realm && !faceShown && identities.length > 0 && <p className="re__note">{w.inGroup ? `It shows ${w.your} default identity until one is picked.` : 'It shows your default identity until you pick one.'}</p>}
                            {!readOnly && mayEditFace && (
                                <NewIdentity groupId={groupId} onMade={(f) => { setMadeFaces((m) => [...m, f]); setFaceId(f.id); onFacesChanged?.(); }} />
                            )}
                            {!mayEditFace && <p className="re__note">Only a group admin chooses which identity the group presents.</p>}
                        </Section>
                        </div>

                        <div className="re__step" data-step="3">
                        <Section n="03" title={<>Nodes <VerbInfo /></>} aside={heldWords(held, keyOf)}
                            hint={readOnly ? null : `Add the nodes of ${w.your} identity graph it holds: by schema and by node, as many as you like, or all nodes at once. Then tap what may be done with them.`}>
                            {mode === 'all' ? (<>
                                <AllRow held={held} readOnly={readOnly} edit={edit} onRemove={() => chooseMode('some')} />
                                {/* a realm saved with more on some schemas or nodes than on all: shown, never hidden */}
                                {(held.schemas.size > 0 || held.singles.size > 0) && (<>
                                    <div className="gp-rcpt__grp re__sub">And more on these</div>
                                    {held.schemas.size > 0 && <SchemaRows ids={schemaRows.filter((id) => held.schemas.has(id))} held={held} orig={orig} keyOf={keyOf} labelOf={labelOf} readOnly={readOnly} edit={edit}
                                        onRemove={(ids) => { ids.forEach((id) => dropRow('schemas', id)); edit((b) => ids.reduce((y, id) => NODE_VERBS.reduce((x, v) => setSchemaVerb(x, id, v, false), y), b)); }} />}
                                    {held.singles.size > 0 && <NodeRows ids={nodeRows.filter((id) => held.singles.has(id))} held={held} nodeInfo={nodeInfo} readOnly={readOnly} edit={edit}
                                        onRemove={(id) => { dropRow('nodes', id); edit((b) => NODE_VERBS.reduce((x, v) => setNodeVerb(x, id, v, false), b)); }} />}
                                </>)}
                            </>) : (<>
                                {!schemaRows.length && !nodeRows.length && <p className="re__empty">None yet. Add by schema, by node, or all nodes, or leave it empty if it needs only the permissions below.</p>}
                                {schemaRows.length > 0 && (<>
                                    <div className="gp-rcpt__grp re__sub">By schema</div>
                                    <SchemaRows ids={schemaRows} held={held} orig={orig} keyOf={keyOf} labelOf={labelOf} readOnly={readOnly} edit={edit}
                                        onRemove={(ids) => { ids.forEach((id) => dropRow('schemas', id)); edit((b) => ids.reduce((y, id) => NODE_VERBS.reduce((x, v) => setSchemaVerb(x, id, v, false), y), b)); }} />
                                </>)}
                                {nodeRows.length > 0 && (<>
                                    <div className="gp-rcpt__grp re__sub">By node</div>
                                    <NodeRows ids={nodeRows} held={held} nodeInfo={nodeInfo} readOnly={readOnly} edit={edit}
                                        onRemove={(id) => { dropRow('nodes', id); edit((b) => NODE_VERBS.reduce((x, v) => setNodeVerb(x, id, v, false), b)); }} />
                                </>)}
                                {!readOnly && (
                                    <AddNodes key={addKey} schemas={schemas} groupId={groupId}
                                        takenSchemas={new Set(schemaRows.map(keyOf))} takenNodes={new Set(nodeRows)} startOpen={!schemaRows.length && !nodeRows.length}
                                        onSchema={(x) => { const ids = familyOf(x.id); ids.forEach((id) => addRow('schemas', id)); edit((b) => ids.reduce((y, id) => setSchemaVerb(y, id, 'read', true, orig), b)); }}
                                        onNode={(n) => {
                                            setNodeInfo((m) => ({ ...m, [String(n.id)]: { title: nameOfNode(n), schema: n.schema_name || schemaNames[n.schema_id] } }));
                                            addRow('nodes', String(n.id));
                                            edit((b) => setNodeVerb(b, String(n.id), 'read', true));
                                        }}
                                        onEvery={() => chooseMode('all')} />
                                )}
                            </>)}
                        </Section>
                        </div>

                        <div className="re__step" data-step="4">
                        <Section n="04" title={`Beyond ${w.your} nodes`} aside={onCount(PERMISSIONS) ? plural(onCount(PERMISSIONS), 'permission') : null}
                            hint={readOnly ? null : w.inGroup
                                ? `Add only what the app or person needs, such as using ${w.your} plugins’ tools. Anything not added stays off.`
                                : 'Add only what the app or person needs, such as using your plugins’ tools or seeing your friends. Anything you do not add stays off.'}>
                            <Beyond blob={blob} orig={orig} readOnly={readOnly} edit={edit} groupId={groupId} />
                        </Section>
                        </div>


                        <div className="re__step" data-step="5">
                        <Section n="05" title="Check it" hint={readOnly ? null : 'What it holds, as anyone shared from it would be granted it.'}>
                            <ViewTabs value={review} onChange={setReview} tabs={[['summary', 'Summary'], ['yaml', 'YAML']]} />
                            {review === 'summary' ? (
                                <div className="re__check">
                                    <div className="re__check-head">
                                        <span className="re__colour-chip" style={{ '--re-hue': hue }} />
                                        <b>{shown || 'Unnamed realm'}</b>
                                        {description && <span>{description}</span>}
                                    </div>
                                    <AccessList access={withLinks(blob)} schemaNames={schemaNames} loadTitles={loadTitles}
                                        empty="Nothing yet. Anything shared from it gets nothing." />
                                    <p className="re__check-foot">{faceShown ? `Appears as ${faceName(faceShown) || 'the identity you chose'}` : realm || !needFace ? `Appears as ${w.your} default identity` : 'How it appears is not chosen yet'}</p>
                                </div>
                            ) : (<>
                                <pre className="re__yaml-out">{yamlShown(withLinks(blob))}</pre>
                                {!readOnly && <button type="button" className="re__quiet" onClick={openImport}>Paste YAML instead</button>}
                            </>)}
                        </Section>
                        </div>
                    </>)}
                </div>
                </div>

                {impact && (() => {
                    const list = (xs) => `${xs.slice(0, 8).join(', ')}${xs.length > 8 ? ` and ${xs.length - 8} more` : ''}`;
                    const title = impact.narrows.length && impact.widens.length ? 'This changes what people already have'
                        : impact.widens.length ? 'This widens what people already have' : 'This narrows what people already have';
                    return (
                        <div className="re__impact" role="alertdialog" aria-label={title}>
                            <b>{title}.</b>
                            {impact.narrows.length > 0 && <span>{list(impact.narrows)} will lose what the realm no longer holds.</span>}
                            {impact.widens.length > 0 && <span>{list(impact.widens)} will get what you are adding, straight away.</span>}
                        </div>
                    );
                })()}
                {undoImport && !importing && (
                    <p className="re__impact" role="status">
                        <span>Replaced by the pasted YAML. Nothing is saved until you save.</span>
                        <button type="button" className="re__quiet" onClick={undoPaste}>Undo</button>
                    </p>
                )}
                {leaving && (
                    <div className="re__impact" role="alertdialog" aria-label="Discard your changes?">
                        <b>Discard your changes?</b>
                        <span>{realm ? `What you changed in ${realm.name} is not saved.` : 'This realm is not created yet.'}</span>
                        <span className="re__row-end">
                            <button type="button" className="gp-link re__quiet" onClick={() => setLeaving(false)}>Keep editing</button>
                            <button type="button" className="re__btn" onClick={() => onDone(false)}>Discard</button>
                        </span>
                    </div>
                )}
                {error && <p className="re__error">{error}</p>}
                {!importing && (
                    <Decide
                        className="re__decide"
                        denyLabel={readOnly ? 'Done' : 'Cancel'}
                        onDeny={() => (impact ? setImpact(null) : close())}
                        confirmLabel={readOnly ? null : working ? 'Saving…' : impact ? 'Save anyway' : realm ? 'Save' : 'Create realm'}
                        disabled={!shown || (needFace && !faceShown) || working}
                        danger={!!impact}
                        onConfirm={() => (impact ? (setImpact(null), save(true)) : save(false))}
                    />
                )}
            </ConsentFrame>
        </Modal>
        </Words.Provider>
    );
}

// ⓘ: what read, create, edit, delete and propose let them do
function VerbInfo() {
    const w = useContext(Words);
    const [open, setOpen] = useState(false);
    const box = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', away);
        return () => document.removeEventListener('mousedown', away);
    }, [open]);
    return (
        <span className="re__info" ref={box}>
            <button type="button" className="re__info-btn" aria-label="What the verbs mean" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
                <Info size={13} strokeWidth={1.8} aria-hidden="true" />
            </button>
            {open && (
                <span className="re__info-pop" role="note">
                    {verbMeans(w).map(([v, means]) => (
                        <span key={v} className="re__info-row"><em className={`gp-vtag is-${v} re__vtag is-on`}>{v}</em><span>{means}</span></span>
                    ))}
                </span>
            )}
        </span>
    );
}

// ── nodes: one row per schema (or all data, or a node), its verbs as tags ──

function VerbTags({ verbs, inherited = [], readOnly, onToggle, label }) {
    const hints = verbHint(useContext(Words));
    return (
        <span className="re__verbs" role="group" aria-label={`What may be done with ${label}`}>
            {NODE_VERBS.map((v) => {
                const always = inherited.includes(v);
                const on = always || verbs.has(v);
                return (
                    <button key={v} type="button" aria-pressed={on} disabled={readOnly || always}
                        title={always ? `${v}: already allowed on all nodes` : `${v}: ${hints[v]}`}
                        className={`gp-vtag is-${v} re__vtag${on ? ' is-on' : ''}`} onClick={() => onToggle(v, !on)}>{v}</button>
                );
            })}
        </span>
    );
}

const Remove = ({ readOnly, onPress }) => !readOnly && (
    <button type="button" className="re__remove" aria-label="Remove" title="Remove" onClick={onPress}><X size={13} strokeWidth={1.8} /></button>
);

// ALL NODES: one row whose verbs reach every node in your graph
function AllRow({ held, readOnly, edit, onRemove }) {
    const w = useContext(Words);
    const on = held.all.length > 0;
    return (
        <div className="gp-bound re__rows">
            <div className={`re__row${on ? '' : ' is-idle'}`}>
                <div className="gp-brow is-lit">
                    <span className="nx-rules__icon"><Glyphed size={22}><LayoutGrid size={11} strokeWidth={1.8} /></Glyphed></span>
                    <span className="gp-brow__lab is-cap">All nodes in {w.your} graph</span>
                    <VerbTags verbs={new Set(held.all)} readOnly={readOnly} label="all nodes"
                        onToggle={(v, x) => edit((b) => setAllVerb(b, v, x))} />
                    <Remove readOnly={readOnly} onPress={onRemove} />
                </div>
                <p className="nx-rules__more">{on ? (w.inGroup ? `Every schema ${w.youHave}, and any added later.` : 'Every schema you have, and any you add later.') : 'Nothing allowed yet. Tap a verb.'}</p>
            </div>
        </div>
    );
}

function SchemaRows({ ids, held, orig, keyOf = (id) => id, labelOf = (id) => id, readOnly, edit, onRemove }) {
    const [open, setOpen] = useState(null);             // "<key>:fields" · "<key>:filter"
    const flip = (key) => setOpen((o) => (o === key ? null : key));
    if (!ids.length) return <p className="re__empty">No schemas yet.</p>;
    // one row per schema: its versions (each its own id) travel together
    const rows = [];
    for (const id of ids) {
        const name = keyOf(id);
        const row = rows.find((r) => r.name === name);
        if (row) row.ids.push(id); else rows.push({ name, ids: [id] });
    }
    return (
        <div className="gp-bound re__rows">
            {rows.map(({ name, ids: rowIds }) => {
                const id = rowIds[0];
                const all = (fn) => (b) => rowIds.reduce((x, each) => fn(x, each), b);
                const verbs = held.schemas.get(id) || new Set();
                const mask = held.masks[id] || {};
                const label = labelOf(id) || 'A schema';
                const masked = Array.isArray(mask.fields);
                const filtered = !!(mask.filter && Object.keys(mask.filter).length);
                const fieldsOpen = open === `${name}:fields`;
                const filterOpen = open === `${name}:filter`;
                return (
                    <div key={name} className={`re__row${verbs.size ? '' : ' is-idle'}`}>
                        <div className="gp-brow is-lit">
                            <span className="nx-rules__icon"><NodeMark schemaId={id} schema={label} size={22} /></span>
                            <span className="gp-brow__lab">{label}</span>
                            <VerbTags verbs={verbs} inherited={held.all} readOnly={readOnly} label={label}
                                onToggle={(v, on) => edit(all((b, each) => setSchemaVerb(b, each, v, on, orig)))} />
                            <Remove readOnly={readOnly} onPress={() => onRemove(rowIds)} />
                        </div>
                        {/* what of it is read: which fields, and which entries */}
                        {verbs.has('read') && (!readOnly || masked || filtered) && (
                            <p className="nx-rules__more re__narrow">
                                <button type="button" className={`re__fieldsbtn${masked ? ' is-masked' : ''}`} disabled={readOnly}
                                    aria-expanded={fieldsOpen} title="Which fields it may read" onClick={() => flip(`${name}:fields`)}>
                                    {masked ? `Only ${mask.fields.join(', ')}` : 'all fields'}
                                </button>
                                <button type="button" className={`re__fieldsbtn${filtered ? ' is-masked' : ''}`} disabled={readOnly}
                                    aria-expanded={filterOpen} title="Only the entries where a field matches" onClick={() => flip(`${name}:filter`)}>
                                    {filtered ? `Only where ${filterWords(mask.filter)}` : 'all entries'}
                                </button>
                            </p>
                        )}
                        {fieldsOpen && verbs.has('read') && <FieldPicker schemaId={rowIds[rowIds.length - 1]} picked={mask.fields} onPick={(f) => edit(all((b, each) => setFields(b, each, f)))} />}
                        {filterOpen && verbs.has('read') && <FilterEditor schemaId={rowIds[rowIds.length - 1]} filter={mask.filter} onChange={(f) => edit(all((b, each) => setFilter(b, each, f)))} />}
                    </div>
                );
            })}
        </div>
    );
}

function NodeRows({ ids, held, nodeInfo, readOnly, edit, onRemove }) {
    if (!ids.length) return <p className="re__empty">None yet. Add one to share it without the rest of its schema.</p>;
    return (
        <div className="gp-bound re__rows">
            {ids.map((id) => {
                const verbs = held.singles.get(id) || new Set();
                const info = nodeInfo[id] || {};
                return (
                    <div key={id} className={`re__row${verbs.size ? '' : ' is-idle'}`}>
                        <div className="gp-brow is-lit">
                            <span className="nx-rules__icon">{info.schema ? <NodeMark schema={info.schema} size={22} /> : <Glyphed size={22}><Target size={11} strokeWidth={1.8} /></Glyphed>}</span>
                            <span className="gp-brow__lab is-cap">{info.title || 'A node'}</span>
                            {info.schema && <span className="re__fieldsbtn is-still">{info.schema}</span>}
                            <VerbTags verbs={verbs} inherited={held.all} readOnly={readOnly} label={info.title || 'this node'}
                                onToggle={(v, on) => edit((b) => setNodeVerb(b, id, v, on))} />
                            <Remove readOnly={readOnly} onPress={() => onRemove(id)} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// the fields of one schema, read from its JSON schema: each with its kind (and its
// values, when it names them)
function useSchemaFields(schemaId) {
    const [fields, setFields] = useState(null);
    useEffect(() => {
        let live = true;
        api.request(`/schemas/${schemaId}`)
            .then((s) => {
                const props = s?.json_schema?.properties || {};
                if (live) {
                    setFields(Object.entries(props).filter(([k]) => !k.startsWith('_'))
                        .map(([name, prop]) => ({ name, kind: kindOfField(name, prop || {}), values: Array.isArray(prop?.enum) ? prop.enum.map(String) : null })));
                }
            })
            .catch(() => { if (live) setFields([]); });
        return () => { live = false; };
    }, [schemaId]);
    return fields;
}

const BLANK = { field: '', op: 'eq', value: '' };

// one condition's value, as its field's kind wants it
function CondValue({ kind, op, values, value, onChange }) {
    if (VALUELESS.has(op)) return <span className="re__cond-none" />;
    if (op === 'last' || op === 'older' || op === 'next') {
        const [n = '', unit = 'days'] = String(value).trim().split(/\s+/);
        return (
            <span className="re__cond-span">
                <input type="number" min="1" step="1" value={n} onChange={(e) => onChange(`${e.target.value} ${unit}`)} aria-label="How many" placeholder="30" />
                <Picker fill label="Of what" value={unit} onChange={(u) => onChange(`${n} ${u}`)} options={SPAN_UNITS.map(([u]) => u)} />
            </span>
        );
    }
    if (kind === 'date') return <input type="date" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Value" />;
    if (kind === 'number') return <input type="number" step="any" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Value" placeholder="a number" />;
    if (kind === 'yesno') {
        return (
            <Picker fill label="Value" placeholder="choose…" value={value} onChange={onChange} options={['true', 'false']} />
        );
    }
    if (kind === 'choice' && op !== 'in') {
        return (
            <Picker fill label="Value" placeholder="choose…" value={value} onChange={onChange} options={values} />
        );
    }
    if (kind === 'choice') {
        const on = new Set(String(value).split(',').map((x) => x.trim()).filter(Boolean));
        return (
            <span className="re__cond-chips" role="group" aria-label="Value">
                {values.map((v) => (
                    <button key={v} type="button" aria-pressed={on.has(v)} className={`re__chip${on.has(v) ? ' is-on' : ''}`}
                        onClick={() => { const n = new Set(on); if (n.has(v)) n.delete(v); else n.add(v); onChange([...n].join(', ')); }}>{v}</button>
                ))}
            </span>
        );
    }
    return <input value={value} onChange={(e) => onChange(e.target.value)} aria-label="Value" placeholder={op === 'in' ? 'run, swim, ride' : 'a value'} />;
}

// ONLY SOME ENTRIES: conditions on the schema's fields, every one to hold, each
// compared as its kind is: dates by day, numbers by size, true or false, one of
// the values a field names, or text. Read through the realm, an entry that does
// not match is left out.
function FilterEditor({ schemaId, filter, onChange }) {
    const fields = useSchemaFields(schemaId);
    const [rows, setRows] = useState(() => {
        const c = conditionsOf(filter);
        return c && c.length ? c : [BLANK];
    });
    if (conditionsOf(filter) === null) return <p className="nx-rules__more">This filter joins two realms' conditions; change it in the YAML.</p>;
    if (!fields) return <p className="nx-rules__more">Reading its fields…</p>;
    const usable = fields.filter((f) => f.kind !== 'list');
    if (!usable.length) return <p className="nx-rules__more">This schema names no fields to filter on.</p>;
    const put = (next) => { setRows(next); onChange(filterOf(next)); };
    const set = (i, patch) => put(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
    return (
        <div className="re__filter" role="group" aria-label="Only the entries where">
            <span className="re__minor">Only the entries where</span>
            {rows.map((r, i) => {
                const f = usable.find((x) => x.name === r.field);
                const kind = f?.kind || 'text';
                const ops = OPS_BY_KIND[kind].includes(r.op) || !f ? OPS_BY_KIND[kind] : [...OPS_BY_KIND[kind], r.op];
                return (
                    <div key={i} className="re__cond">
                        <Picker fill label="Field" placeholder="a field…" value={r.field} options={usable.map((x) => x.name)}
                            onChange={(name) => { const nf = usable.find((x) => x.name === name); set(i, { field: name, op: OPS_BY_KIND[nf?.kind || 'text'][0], value: '' }); }} />
                        <Picker fill label="How it compares" value={r.op} onChange={(op) => set(i, { op })} disabled={!f}
                            options={ops.map((k) => ({ value: k, label: FILTER_OPS.find(([o]) => o === k)?.[1] || k }))} />
                        <CondValue kind={kind} op={r.op} values={f?.values || []} value={r.value} onChange={(value) => set(i, { value })} />
                        <button type="button" className="re__remove" aria-label="Remove this condition" onClick={() => put(rows.length > 1 ? rows.filter((_, k) => k !== i) : [BLANK])}>
                            <X size={13} strokeWidth={1.8} />
                        </button>
                    </div>
                );
            })}
            <div className="re__cond-foot">
                <button type="button" className="re__quiet" onClick={() => setRows([...rows, BLANK])}>And another</button>
                <span>Applies to what is read through the realm.{rows.some((r) => usable.find((x) => x.name === r.field)?.kind === 'date') ? ' Days are UTC; spans count from today.' : ''}</span>
            </div>
        </div>
    );
}

function FieldPicker({ schemaId, picked, onPick }) {
    const named = useSchemaFields(schemaId);
    const fields = named && named.map((f) => f.name);
    if (!fields) return <p className="nx-rules__more">Reading its fields…</p>;
    if (!fields.length) return <p className="nx-rules__more">This schema names no fields, so it is shared whole.</p>;
    const on = new Set(Array.isArray(picked) ? picked : fields);
    const flip = (f) => {
        const next = new Set(on);
        if (next.has(f)) next.delete(f); else next.add(f);
        const list = fields.filter((x) => next.has(x));
        onPick(list.length === fields.length ? null : list);
    };
    return (
        <div className="re__fields" role="group" aria-label="Fields it may read">
            {fields.map((f) => (
                <button key={f} type="button" aria-pressed={on.has(f)} className={`re__chip${on.has(f) ? ' is-on' : ''}`} onClick={() => flip(f)}>{f}</button>
            ))}
        </div>
    );
}

function Option({ mark, name, note, cap, onPick }) {
    return (
        <button type="button" role="option" aria-selected="false" className="re__option" onClick={onPick}>
            {mark}
            <span className={`re__option-name${cap ? ' is-cap' : ''}`}>{name}</span>
            {note ? <span className="re__option-note">{note}</span> : <Plus size={12} strokeWidth={2} className="re__option-plus" aria-hidden="true" />}
        </button>
    );
}

// ADD NODES: three ways side by side, as tabs, so the other two are always one
// press away: by schema and by node (as many as you like, mixed), or all nodes
// in their place.
const ADD_WAYS = [['schema', 'By schema', Layers], ['node', 'By node', Target], ['all', 'All nodes', LayoutGrid]];

function FindList({ placeholder, children }) {
    const [q, setQ] = useState('');
    const input = useRef(null);
    const term = q.trim().toLowerCase();
    const picked = () => { setQ(''); input.current?.focus(); };
    return (<>
        <FindBar compact className="nx-preview__search" inputRef={input} value={q} onChange={setQ} placeholder={placeholder} />
        <div className="re__options" role="listbox" aria-label="What it found">{children(term, picked)}</div>
    </>);
}

function AddNodes({ schemas, groupId, takenSchemas, takenNodes, startOpen, onSchema, onNode, onEvery }) {
    const w = useContext(Words);
    const [open, setOpen] = useState(startOpen);
    const [way, setWay] = useState('schema');
    const hasRows = takenSchemas.size > 0 || takenNodes.size > 0;
    if (!open) {
        return <button type="button" className="re__add" onClick={() => setOpen(true)}><Plus size={13} strokeWidth={2} aria-hidden="true" /> Add nodes</button>;
    }
    return (
        <div className="re__adder re__addnodes">
            <Segmented
                label="Add nodes" fill className="re__ways" value={way} onChange={setWay}
                options={ADD_WAYS.map(([id, label, Icon]) => ({ id, label, icon: <Icon strokeWidth={1.8} /> }))}
            />
            {way === 'schema' && (
                <FindList key="schema" placeholder="Find a schema">
                    {(term, picked) => {
                        // every schema, once each, in a list that scrolls; typing narrows it
                        const free = schemas.filter((x) => !takenSchemas.has(x.key) && (!term || x.name.toLowerCase().includes(term)));
                        return (<>
                            {free.map((x) => <Option key={x.key} name={x.name} mark={<NodeMark schemaId={x.id} schema={x.name} size={20} />} onPick={() => { onSchema(x); picked(); }} />)}
                            {term && !free.length && <p className="re__option-more">No schema by that name.</p>}
                            {!term && !free.length && <p className="re__option-more">{w.inGroup ? `Every schema ${w.youHave} is here.` : 'Every schema you have is here.'}</p>}
                        </>);
                    }}
                </FindList>
            )}
            {way === 'node' && (
                <FindList key="node" placeholder="Find a node by its title">
                    {(term, picked) => <NodeResults term={term} taken={takenNodes} groupId={groupId} onPick={(n) => { onNode(n); picked(); }} />}
                </FindList>
            )}
            {way === 'all' && (
                <div className="re__addall">
                    <p>{hasRows ? `Everything in ${w.your} graph, now and later, in place of the schemas and nodes above. Remove it to have them back.` : `Everything in ${w.your} graph, now and later.`}</p>
                    <button type="button" className="re__btn" onClick={onEvery}>Use all nodes</button>
                </div>
            )}
            <button type="button" className="re__quiet re__adder-close" onClick={() => setOpen(false)}>Close</button>
        </div>
    );
}

// an identity's picture, or the person glyph, square
function FaceTile({ identity }) {
    const [failed, setFailed] = useState(false);
    if (identity.avatar_url && !failed) return <img className="gp-explain__tile gp-face__tile gp-face__photo" src={identity.avatar_url} alt="" onError={() => setFailed(true)} />;
    return <span className="gp-explain__tile gp-face__tile"><UserRound size={12} strokeWidth={1.7} aria-hidden="true" /></span>;
}

// MAKE AN IDENTITY here, for this realm: a name, and it is made and chosen
function NewIdentity({ groupId, onMade }) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    if (!open) {
        return (
            <button type="button" className="re__add re__newface" onClick={() => setOpen(true)}>
                <UserPlus size={13} strokeWidth={1.8} aria-hidden="true" /> Use a new identity
            </button>
        );
    }
    const make = async () => {
        const title = name.trim();
        if (!title) return;
        setBusy(true); setErr(null);
        try {
            const body = { title, value_json: { display_name: title }, schema_name: 'profile' };
            const n = groupId ? await api.createGroupNode(groupId, body) : await api.request('/nodes', { method: 'POST', body });
            const node = n?.node || n;
            onMade({ id: String(node.id), name: title, node });
            setOpen(false); setName('');
        } catch (e) { setErr(e?.message || 'Could not make it'); }
        setBusy(false);
    };
    return (
        <div className="re__adder re__newface-form">
            <label className="re__field">
                <span className="re__minor">A new identity</span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="What it is called, such as Work me" aria-label="New identity's name" autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); make(); } }} />
            </label>
            {err && <p className="re__note">{err}</p>}
            <div className="re__row-end">
                <button type="button" className="re__quiet" onClick={() => { setOpen(false); setName(''); }}>Cancel</button>
                <button type="button" className="re__btn" disabled={!name.trim() || busy} onClick={make}>{busy ? 'Making…' : 'Make it and use it'}</button>
            </div>
        </div>
    );
}

function NodeResults({ term, taken, groupId, onPick }) {
    const [found, setFound] = useState(null);
    useEffect(() => {
        if (term.length < 2) { setFound(null); return undefined; }
        let live = true;
        const t = setTimeout(() => {
            const call = groupId ? api.getGroupNodes(groupId, { search: term, limit: 8 }) : api.getNodes({ search: term, limit: 8 });
            Promise.resolve(call).then((r) => { if (live) setFound(Array.isArray(r) ? r : r?.nodes || []); }).catch(() => { if (live) setFound([]); });
        }, 220);
        return () => { live = false; clearTimeout(t); };
    }, [term, groupId]);
    if (term.length < 2) return <p className="re__option-more">Type two letters of its title.</p>;
    if (!found) return <p className="re__option-more">Looking…</p>;
    const nodes = found.filter((n) => !taken.has(String(n.id)));
    if (!nodes.length) return <p className="re__option-more">No node by that title.</p>;
    return nodes.map((n) => <Option key={n.id} cap name={nameOfNode(n)} note={n.schema_name || 'node'}
        mark={<NodeMark schemaId={n.schema_id} schema={n.schema_name} size={20} />} onPick={() => onPick(n)} />);
}

// ── beyond nodes: each permission a row, added from a list; the rest is a no ──
function Beyond({ blob, orig, readOnly, edit, groupId }) {
    const w = useContext(Words);
    const OTHER_HINT = otherHint(w);
    const [adding, setAdding] = useState(false);
    const [choosing, setChoosing] = useState(false);
    const [plugins, setPlugins] = useState(null);
    const has = (res, verb) => !!(isObj(blob[res]) && blob[res][verb] !== undefined);
    const added = PERMISSIONS.filter(([res, verb]) => has(res, verb));
    // a group's realm is never offered a person's own friends or groups (one already
    // there still shows, so it can be taken off)
    const left = PERMISSIONS.filter(([res, verb]) => !has(res, verb) && !(groupId && PERSONAL_ONLY.has(`${res}.${verb}`)));
    const allow = blob.plugins?.execute?.plugin_ids;
    // THE PLUGINS SET UP IN THIS SPACE, not every plugin there is: a realm can only
    // let something run what you have. Read as soon as running plugins is added,
    // so a space with none says so at once.
    const running = has('plugins', 'execute');
    useEffect(() => {
        if (!running || plugins) return;
        loadPluginList(groupId).then((list) => setPlugins(list.filter((p) => p.installed)));
    }, [running, plugins, groupId]);
    const storeHref = `${groupId ? `/group/${groupId}` : ''}/plugins/store`;
    // the chosen plugins by name ("Only Mailroom and Notion Pages"), from those installed here
    const pluginNames = Object.fromEntries((plugins || []).map((p) => [String(p.id), { name: p.name }]));
    const flipPlugin = (id) => edit((b) => {
        const next = clone(b);
        const ex = next.plugins.execute;
        const ids = new Set((ex.plugin_ids || []).map(String));
        if (ids.has(id)) ids.delete(id); else ids.add(id);
        if (ids.size) ex.plugin_ids = [...ids]; else delete ex.plugin_ids;
        return next;
    });
    const mark = (Icon) => <Glyphed size={22}><Icon size={11} strokeWidth={1.8} /></Glyphed>;
    return (<>
        {added.length === 0 ? <p className="re__empty">None. It can use only the nodes above.</p> : (
            <div className="gp-bound re__rows">
                {added.map(([res, verb, Icon]) => {
                    const word = VERB_LABELS[`${res}.${verb}`];
                    const isPlugins = res === 'plugins' && verb === 'execute';
                    return (
                        <div key={word} className="re__row">
                            <div className="gp-brow is-lit">
                                <span className="nx-rules__icon">{mark(Icon)}</span>
                                <span className="gp-brow__lab is-cap">{word}</span>
                                <Remove readOnly={readOnly} onPress={() => { if (isPlugins) setChoosing(false); edit((b) => setVerb(b, res, verb, false)); }} />
                            </div>
                            <p className="nx-rules__more">{OTHER_HINT[`${res}.${verb}`]}</p>
                            {isPlugins && plugins?.length === 0 && (
                                <p className="nx-rules__more re__more">
                                    {groupId ? 'This group has no plugins set up yet.' : 'You have no plugins set up yet.'}
                                    <a className="re__quiet" href={storeHref}>Get a plugin</a>
                                </p>
                            )}
                            {isPlugins && plugins?.length !== 0 && (
                                <p className="nx-rules__more re__more">
                                    {allow?.length ? onlyPluginsLine(pluginNames, allow) : plugins ? `Every plugin ${w.inGroup ? w.youHave : 'you have'} (${plugins.length})` : `Every plugin ${w.inGroup ? w.youHave : 'you have'}`}
                                    {!readOnly && plugins?.length > 0 && <button type="button" className="re__quiet" onClick={() => setChoosing((v) => !v)}>{choosing ? 'Done' : 'Choose plugins'}</button>}
                                </p>
                            )}
                            {isPlugins && choosing && (
                                <div className="re__fields" role="group" aria-label="Plugins whose tools it may use">
                                    {plugins === null && <span className="re__option-more">{w.inGroup ? 'Reading its plugins…' : 'Reading your plugins…'}</span>}
                                    {(plugins || []).map((p) => {
                                        const id = String(p.id);
                                        const picked = (allow || []).map(String).includes(id);
                                        return <button key={id} type="button" aria-pressed={picked} className={`re__chip${picked ? ' is-on' : ''}`} onClick={() => flipPlugin(id)}>{p.name || id}</button>;
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        )}
        {!readOnly && left.length > 0 && (adding ? (
            <div className="re__adder">
                <div className="re__options" role="listbox" aria-label="Add a permission">
                    {left.map(([res, verb, Icon]) => (
                        <button key={`${res}.${verb}`} type="button" role="option" aria-selected="false" className="re__option re__perm"
                            onClick={() => { edit((b) => setVerb(b, res, verb, true, { orig })); setAdding(false); }}>
                            {mark(Icon)}
                            <span className="re__perm-text">
                                <span className="re__perm-name">{VERB_LABELS[`${res}.${verb}`]}</span>
                                <span className="re__perm-hint">{OTHER_HINT[`${res}.${verb}`]}</span>
                            </span>
                            <Plus size={12} strokeWidth={2} className="re__option-plus" aria-hidden="true" />
                        </button>
                    ))}
                </div>
                <button type="button" className="re__quiet re__adder-close" onClick={() => setAdding(false)}>Close</button>
            </div>
        ) : (
            <button type="button" className="re__add" onClick={() => setAdding(true)}>
                <Plus size={13} strokeWidth={2} aria-hidden="true" /> Add a permission
            </button>
        ))}
    </>);
}

// ── the colour: Auto picks one none of your realms use; the rest say who has them ──
function ColourPicker({ value, auto, taken, onChange }) {
    const [open, setOpen] = useState(false);
    const [over, setOver] = useState(null);     // the colour under the pointer
    const box = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', away);
        return () => document.removeEventListener('mousedown', away);
    }, [open]);
    const current = value || auto;
    const pick = (c) => { onChange(c); setOpen(false); };
    const sharing = value ? taken.get(value.toLowerCase()) : null;
    const said = (c) => { const by = taken.get(c.toLowerCase()); return by ? `${colourName(c)} · ${by.join(', ')}` : colourName(c); };
    return (
        <div className="re__colour">
            <span className="re__minor">Colour</span>
            <span className="re__colour-anchor" ref={box}>
                <button type="button" className="re__colour-btn" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen((v) => !v)}>
                    <span className="re__colour-chip" style={{ '--re-hue': current }} />
                    <span className="re__colour-name">{value ? colourName(value) : <>Auto <i>· {colourName(auto)}</i></>}</span>
                    <ChevronDown size={12} strokeWidth={1.8} aria-hidden="true" />
                </button>
                {open && (
                    <div className="re__colour-pop" onMouseLeave={() => setOver(null)}>
                        <button type="button" className="re__colour-auto" aria-pressed={!value} onClick={() => pick(null)} onMouseEnter={() => setOver(null)}>
                            <span className="re__colour-chip" style={{ '--re-hue': auto }} />
                            Auto
                        </button>
                        <div className="re__colour-grid" role="listbox" aria-label="Colours">
                            {COLOURS.map(([c, n]) => {
                                const by = taken.get(c.toLowerCase());
                                return (
                                    <button key={c} type="button" role="option" aria-selected={!!value && value.toLowerCase() === c.toLowerCase()}
                                        aria-label={by ? `${n}, used by ${by.join(', ')}` : n}
                                        className={`re__colour-cell${by ? ' is-taken' : ''}`} style={{ '--re-hue': c }}
                                        onMouseEnter={() => setOver(c)} onFocus={() => setOver(c)} onClick={() => pick(c)}>
                                        <span />
                                    </button>
                                );
                            })}
                        </div>
                        <p className="re__colour-say">{over ? said(over) : `Auto picks ${colourName(auto)}`}</p>
                    </div>
                )}
            </span>
            {sharing && <span className="re__colour-also">also worn by {sharing.join(', ')}</span>}
        </div>
    );
}
